/**
 * @file PricingEarlierSales.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description Pricing & Costing: a line's earlier sales under its sale
 *              options, each with ITS delivery date, price, cost and margin
 *              (Cam, 28 Oct 2026 — D3 note: "Ensure previous price info for rep
 *              view and standard shows previous cost and margin too"; D10 note:
 *              "ensure the previous sale price also shows that date's/ order's
 *              cost and the consequent margin wherever we can … if we can
 *              provide a cost from Xero source POs and bills and sales prices
 *              from customers' sales invoices then we should make every effort
 *              to do so").
 *
 *   POST /api/pricing-costing/earlier-sales (services/pricingCostingService
 *   .earlierSales → priceRequestService.previousSalesFor, the very earlier
 *   sales a rep's request shows): up to three, the same recipe first, never
 *   this line's own order. Each price is per ITS line's unit and says so; the
 *   cost is that sale's own — the bill on the PO line it was bought on (a
 *   Xero-made invoice through its linked PO), else the PO line, else the
 *   order line's cost from stock, else the till's — in the sale's unit.
 *   Costs only for costs.view. Read only; cached per line.
 *
 *   1.0.1 (2026-10-28, N6): what each figure is per by the one rule
 *   (utils/unitPriceLabel.perUnitOfLabel) — " (unit not set)", never "/unit?".
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { money } from '../utils/pricingCosting';
import { perUnitOfLabel } from '../utils/unitPriceLabel';
import { formatDate } from '../utils/dateFormatters';

const cache = new Map();
const COST_WORDS = { bill: 'bill', po: 'PO (not billed yet)', order: 'order line, from stock', till: 'till' };

/**
 * @param {{ line: { key, accountId, itemCode, orderId?, orderLineId? }, seeCosts: boolean, fallbackPer?: string }} props
 *   fallbackPer  this line's "/T" etc., for an earlier sale whose unit was not recorded
 */
function PricingEarlierSales({ line, seeCosts, fallbackPer = '' }) {
  const id = `${line.key}|${line.accountId}|${line.itemCode}`;
  const [state, setState] = useState(() => (cache.has(id) && cache.get(id).done ? cache.get(id).done : { loading: true, error: null, sales: [] }));

  useEffect(() => {
    let gone = false;
    if (!cache.has(id)) {
      const p = fetch(`${API_URL}/api/pricing-costing/earlier-sales`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ lines: [line] })
      }).then(r => r.json().then(b => {
        if (!r.ok || !b.success) throw new Error(b.error || 'Earlier sales not read');
        const done = { loading: false, error: null, sales: (b.lines?.[0]?.sales) || [] };
        cache.set(id, { promise: p, done });
        return done;
      })).catch(err => { cache.delete(id); throw err; });
      cache.set(id, { promise: p });
    }
    const entry = cache.get(id);
    if (entry.done) { setState(entry.done); return undefined; }
    entry.promise
      .then(done => { if (!gone) setState(done); })
      .catch(err => { if (!gone) setState({ loading: false, error: err.message, sales: [] }); });
    return () => { gone = true; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.loading) return <div className="pc-earlier"><span className="pc-muted">Reading earlier sales…</span></div>;
  if (state.error) return <div className="pc-earlier"><span className="pc-flag err"><span className="material-symbols-rounded">error</span>{state.error}</span></div>;
  if (!state.sales.length) return <div className="pc-earlier"><span className="pc-muted">No earlier sale of this product to this customer.</span></div>;
  return (
    <div className="pc-earlier">
      <h6><span className="material-symbols-rounded">history</span>Earlier sales — each with its own cost and margin</h6>
      {state.sales.map((x, i) => {
        // A sale whose unit was not recorded (an invoice made in Xero with no
        // weight on its line or item): read in this line's unit, and said so.
        const per = x.unit ? perUnitOfLabel(x.unit).suffix : (fallbackPer ? `${fallbackPer} (assumed)` : perUnitOfLabel('').suffix);
        const when = x.deliveryDate ? `delivered ${formatDate(x.deliveryDate)}` : `${x.dateFrom === 'invoice' ? 'invoiced' : 'ordered'} ${formatDate(x.date)}`;
        const doc = [x.invoiceNo, x.orderNumber && x.orderNumber !== x.invoiceNo ? x.orderNumber : null, x.poNumber].filter(Boolean).join(' · ');
        return (
          <div key={x.ref || i} className="pc-earlier-row">
            <span className="pc-earlier-price"><b>{money(x.price)}</b><span className="pc-unit" title={x.unit ? '' : 'That sale\'s unit isn\'t recorded — read in this line\'s unit (the same product)'}>{per}</span>
              {!x.recipeMatch && <span className="pc-tag" title="Another additive recipe">other recipe</span>}</span>
            <span className="pc-muted">{when}{doc ? ` · ${doc}` : ''}{x.priceFrom === 'order' ? ' · the order\'s price (not invoiced yet)' : ''}{x.method?.label ? ` · ${x.method.label}` : ''}</span>
            {seeCosts && (
              x.cost !== null && x.cost !== undefined ? (
                <span className="pc-earlier-cost">
                  cost {money(x.cost)}{per} <span className="pc-muted">({x.costRef || COST_WORDS[x.costFrom] || x.costFrom})</span>
                  {x.margin !== null && x.margin !== undefined && (
                    <> · margin <span className={x.margin < 0 ? 'pc-neg' : ''}>{money(x.margin)}{per}</span>{x.marginPc !== null ? <span className="pc-muted"> ({Number(x.marginPc).toFixed(1)}% on cost)</span> : null}</>
                  )}
                </span>
              ) : <span className="pc-muted">{x.costNote || 'No cost found for that sale'}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default PricingEarlierSales;
